import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const ITEM_INIT = { product_id: '', product_name: '', qty: 1, price: '' };
const INIT = {
  customer_id: '', opportunity_id: '', status: 'draft',
  valid_until: '', discount: '', tax_amount: '', notes: '', items: [{ ...ITEM_INIT }],
};
const LIMIT = 15;
const STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
const statusColor = {
  draft: 'info', sent: 'primary', accepted: 'active', rejected: 'inactive', expired: 'inactive',
};
const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function Quotes({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [customers, setCustomers] = useState([]);
  const [opps, setOpps]         = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => { setTitle('Quotes'); }, [setTitle]);
  useEffect(() => {
    api.get('/customers/all').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/opportunities?limit=200').then(r => setOpps(r.data.data || [])).catch(() => {});
    api.get('/products?limit=500').then(r => setProducts(r.data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, page, limit: LIMIT });
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await api.get(`/quotes?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [search, page, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ ...INIT, items: [{ ...ITEM_INIT }] });
    setEditId(null); setModal(true);
  };

  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/quotes/${row.id}`);
      setForm({
        customer_id: data.customer_id || '', opportunity_id: data.opportunity_id || '',
        status: data.status, valid_until: data.valid_until ? data.valid_until.split('T')[0] : '',
        discount: data.discount || '', tax_amount: data.tax_amount || '',
        notes: data.notes || '',
        items: data.items.length ? data.items.map(i => ({
          product_id: i.product_id || '', product_name: i.product_name,
          qty: i.qty, price: i.price,
        })) : [{ ...ITEM_INIT }],
      });
      setEditId(row.id); setModal(true);
    } catch { alert('Error loading quote'); }
  };

  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const updateItem = (idx, k, v) => {
    setForm(p => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [k]: v };
      if (k === 'product_id' && v) {
        const prod = products.find(pr => String(pr.id) === String(v));
        if (prod) { items[idx].product_name = prod.name; items[idx].price = prod.price; }
      }
      return { ...p, items };
    });
  };
  const addItem    = () => setForm(p => ({ ...p, items: [...p.items, { ...ITEM_INIT }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.qty || 0) * parseFloat(i.price || 0)), 0);
  const grandTotal = subtotal - parseFloat(form.discount || 0) + parseFloat(form.tax_amount || 0);

  const handleSave = async () => {
    if (!form.customer_id) return alert('Please select a customer');
    if (form.items.some(i => !i.product_name)) return alert('All items need a product name');
    setSaving(true);
    try {
      if (editId) await api.put(`/quotes/${editId}`, form);
      else        await api.post('/quotes', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving quote');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Delete quote "${num}"?`)) return;
    try { await api.delete(`/quotes/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Quotes</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Quote
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search quote no. or customer…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 150 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} quotes</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Quote No.</th><th>Customer</th><th>Status</th>
                  <th>Subtotal</th><th>Discount</th><th>Total</th><th>Valid Until</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={9} className="empty">No quotes found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td><strong>{item.quote_number}</strong></td>
                    <td>{item.customer_name || '—'}</td>
                    <td><span className={`badge badge-${statusColor[item.status]}`} style={{ textTransform: 'capitalize' }}>{item.status}</span></td>
                    <td>${fmt(item.subtotal)}</td>
                    <td className="text-muted">-${fmt(item.discount)}</td>
                    <td><strong>${fmt(item.total)}</strong></td>
                    <td>{item.valid_until ? item.valid_until.split('T')[0] : '—'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.quote_number)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: 780 }}>
            <div className="modal-header">
              <h2>{editId ? 'Edit Quote' : 'New Quote'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Customer *</label>
                  <select className="form-control" value={form.customer_id} onChange={e => f('customer_id', e.target.value)}>
                    <option value="">— Select customer —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Opportunity</label>
                  <select className="form-control" value={form.opportunity_id} onChange={e => f('opportunity_id', e.target.value)}>
                    <option value="">— None —</option>
                    {opps.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Valid Until</label>
                  <input type="date" className="form-control" value={form.valid_until} onChange={e => f('valid_until', e.target.value)} />
                </div>
              </div>

              {/* Line Items */}
              <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600 }}>Line Items</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', fontSize: 12 }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Product</th>
                    <th style={{ padding: '6px 8px', width: 80 }}>Qty</th>
                    <th style={{ padding: '6px 8px', width: 110 }}>Unit Price</th>
                    <th style={{ padding: '6px 8px', width: 110 }}>Total</th>
                    <th style={{ padding: '6px 8px', width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px 4px' }}>
                        <select className="form-control" value={item.product_id} onChange={e => updateItem(idx, 'product_id', e.target.value)} style={{ marginBottom: 4 }}>
                          <option value="">— Custom item —</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        {!item.product_id && (
                          <input className="form-control" placeholder="Item name" value={item.product_name} onChange={e => updateItem(idx, 'product_name', e.target.value)} />
                        )}
                        {item.product_id && <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 4 }}>{item.product_name}</div>}
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input type="number" className="form-control" min="1" value={item.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input type="number" className="form-control" min="0" step="0.01" value={item.price} onChange={e => updateItem(idx, 'price', e.target.value)} />
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>
                        ${fmt(parseFloat(item.qty || 0) * parseFloat(item.price || 0))}
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        {form.items.length > 1 && (
                          <button className="btn-icon danger" onClick={() => removeItem(idx)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn btn-ghost" onClick={addItem} style={{ marginBottom: 16 }}>
                + Add Line Item
              </button>

              <div className="form-grid">
                <div className="form-group">
                  <label>Discount ($)</label>
                  <input type="number" className="form-control" min="0" step="0.01" value={form.discount} onChange={e => f('discount', e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Tax Amount ($)</label>
                  <input type="number" className="form-control" min="0" step="0.01" value={form.tax_amount} onChange={e => f('tax_amount', e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Terms, conditions, or notes…" />
                </div>
              </div>

              {/* Totals */}
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '12px 16px', marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: 'var(--text-muted)' }}>
                  <span>Subtotal</span><span>${fmt(subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: 'var(--text-muted)' }}>
                  <span>Discount</span><span>-${fmt(form.discount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: 'var(--text-muted)' }}>
                  <span>Tax</span><span>+${fmt(form.tax_amount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16, borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
                  <span>Total</span><span>${fmt(grandTotal)}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Quote' : 'Create Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
